import { BillingClosureBadges } from "../../components/BillingClosureBadges";
import { billingClosureState } from "../../utils/billingClosureState";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MdAdd, MdCheck, MdClose, MdCloudUpload, MdDelete, MdDownload, MdVisibility } from "react-icons/md";
import { itIT } from "@mui/x-data-grid-pro";
import { PersistentDataGridPro as DataGridPro } from "../../components/DataGrid/PersistentDataGridPro";
import { toast } from "react-toastify";
import api from "../../api/api";
import { Autocomplete } from "../../components/Autocomplete";
import Modal from "../../components/Modal";
import { IipayFasciaDetails } from "../../components/IipayFasciaDetails";
import { IipayFasciaBadge } from "../../components/IipayFasciaBadge";
import { ImportFileTotals } from "../../components/ImportFileTotals";
import { TotalSectionCard } from "../../components/TotalSectionCard";

const mesi = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];
const TIPI = [
  ["Z", "Zucchetti", "bg-blue-100 text-blue-700 border-blue-300"],
  ["I", "iiPay", "bg-green-100 text-green-700 border-green-300"],
  ["B", "BPoint", "bg-purple-100 text-purple-700 border-purple-300"],
];
const isDeleted = (row) => ["esclusa", "eliminata"].includes(row?.stato);
const tipoClass = (tipo) => TIPI.find(([id]) => id === tipo)?.[2] || "bg-gray-100 text-gray-700 border-gray-300";

const sumTotals = (rows) => rows.reduce((totals, row) => {
  if (isDeleted(row)) return totals;
  const value = Number(row.totale) || 0;
  totals.total += value;
  const tipo = String(row.tipologia_fatturazione || "").toUpperCase();
  if (Object.prototype.hasOwnProperty.call(totals.byType, tipo)) totals.byType[tipo] += value;
  return totals;
}, { total: 0, byType: { Z: 0, B: 0, I: 0 } });

export const ImportFile = ({ data, periodo, roleReadOnly = false, onImported }) => {
  const inputRef = useRef(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualIncludeCessati, setManualIncludeCessati] = useState(false);
  const [manualBusy, setManualBusy] = useState(false);
  const [manualOptions, setManualOptions] = useState({ clienti: [], prestazioni: [] });
  const [manual, setManual] = useState({});
  const [manualPriceLoading, setManualPriceLoading] = useState(false);
  const [manualPriceMessage, setManualPriceMessage] = useState("");
  const manualClient = manualOptions.clienti.find((item) => item.codice_univoco === manual.codice_univoco_azienda);
  const manualCodes = manualOptions.prestazioni.filter((item) => manualClient && (!item.tipologia_fatturazione || item.tipologia_fatturazione === manualClient.tipologia_fatturazione || (manualClient.tipologia_fatturazione === "B" && item.tipologia_fatturazione === "Z")));
  const manualValid = manual.codice_univoco_azienda && manualCodes.some((item) => String(item.codice) === String(manual.prestazione))
    && [manual.quantita, manual.importo_unitario].every((value) => value !== "" && value != null && Number.isFinite(Number(value)) && Number(value) >= 0) && !manualPriceLoading;

  const [totalsOpen, setTotalsOpen] = useState(false);
  const [sectionLines, setSectionLines] = useState(null);
  const [sectionTotalsOpen, setSectionTotalsOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pending, setPending] = useState(null);
  const [destinazione, setDestinazione] = useState(data);
  const [preview, setPreview] = useState(null);
  const [prezzi, setPrezzi] = useState({});
  const [detail, setDetail] = useState(null);
  const [previewTipo, setPreviewTipo] = useState(new Set());
  const [previewSearch, setPreviewSearch] = useState("");
  const [previewSort, setPreviewSort] = useState({ field: "cliente", direction: "asc" });
  const [detailTipo, setDetailTipo] = useState(new Set());
  const [detailSearch, setDetailSearch] = useState("");
  const [detailOnlyCessati, setDetailOnlyCessati] = useState(false);
  const [detailShowDeleted, setDetailShowDeleted] = useState(false);
  const [detailShowZero, setDetailShowZero] = useState(false);
  const [detailOnlyPending, setDetailOnlyPending] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [cellDrafts, setCellDrafts] = useState({});
  // Il blocco è per gestionale: il backend valida ogni cliente prima di importare.
  const closure = billingClosureState(periodo, 'import_file');
  const manualClosed = manualClient?.tipologia_fatturazione === 'B' ? closure.bpointClosed : closure.ziClosed;
  const isLineClosed = (row) => row.tipologia_fatturazione === "B"
    ? closure.bpointClosed || row.stato === "chiusa"
    : periodo?.stato === "chiuso";
  const previewRows = useMemo(() => {
    const cerca = previewSearch.trim().toLowerCase();
    const rows = (preview?.righe || []).filter((row) => !row.esclusa
      && (previewTipo.size === 0 || previewTipo.has(row.tipologia_fatturazione))
      && (!cerca || [row.codice, row.ragione_sociale, row.prestazione, row.voce, row.descrizione, row.descrizione_import]
        .some((value) => String(value || "").toLowerCase().includes(cerca))));
    const values = {
      tipo: (row) => row.tipologia_fatturazione,
      cliente: (row) => `${row.ragione_sociale || ""} ${row.codice || ""}`,
      prestazione: (row) => `${row.prestazione || row.voce || ""} ${row.descrizione || ""}`,
      quantita: (row) => Number(row.quantita),
      prezzo: (row) => row.prezzo == null ? Number.POSITIVE_INFINITY : Number(row.prezzo),
      origine: (row) => row.origine_prezzo || "prezzo richiesto",
    };
    const getValue = values[previewSort.field] || values.cliente;
    return [...rows].sort((a, b) => {
      const left = getValue(a); const right = getValue(b);
      const result = typeof left === "number" && typeof right === "number"
        ? left - right : String(left || "").localeCompare(String(right || ""), "it", { numeric: true, sensitivity: "base" });
      return previewSort.direction === "asc" ? result : -result;
    });
  }, [preview, previewTipo, previewSearch, previewSort]);
  const sortPreview = (field) => setPreviewSort((current) => ({
    field, direction: current.field === field && current.direction === "asc" ? "desc" : "asc",
  }));
  const sortLabel = (field, label) => `${label}${previewSort.field === field ? (previewSort.direction === "asc" ? " ▲" : " ▼") : ""}`;
  const detailRows = useMemo(() => {
    const cerca = detailSearch.trim().toLowerCase();
    const rows = (detail?.lines || []).filter((row) =>
      (detailShowDeleted || !isDeleted(row))
      && (detailTipo.size === 0 || detailTipo.has(row.tipologia_fatturazione))
      && (!detailOnlyCessati || row.data_fine_elaborazioni)
      && (detailShowZero || (detailShowDeleted && isDeleted(row)) || Number(row.totale) !== 0)
      && (!detailOnlyPending || ["forzata", "bozza"].includes(row.stato)
        || ["quantita", "importo_unitario"].some((field) => {
          const draft = cellDrafts[`${row.id}-${field}`];
          return draft !== undefined && (draft === "" || Number(draft) !== Number(row[field]));
        }))
      && (!cerca || [row.codice_univoco_azienda, row.ragione_sociale, row.prestazione,
        row.descrizione_prestazione, row.descrizione_extra_fattura, row.referente_payroll1, row.referente_assunzioni]
        .some((value) => String(value || "").toLowerCase().includes(cerca))));
    return rows;
  }, [detail, detailTipo, detailSearch, detailShowZero, detailOnlyPending, detailOnlyCessati, detailShowDeleted, cellDrafts]);
  const detailTotals = useMemo(() => sumTotals(detail?.lines || []), [detail]);
  const sectionTotals = useMemo(() => sumTotals(sectionLines || []), [sectionLines]);

  useEffect(() => {
    setSectionLines(null);
    setSectionTotalsOpen(false);
    if (detail || loading || !files.length) return;
    const controller = new AbortController();
    const [anno, mese] = data.split("-");
    api.get("/billing/import-files/section-lines", { params: { anno, mese }, signal: controller.signal })
      .then(({ data: rows }) => { if (!controller.signal.aborted) setSectionLines(rows); })
      .catch((error) => { if (!controller.signal.aborted) toast.error(error.response?.data?.message || "Errore caricamento totali sezione 6"); });
    return () => controller.abort();
  }, [detail, loading, files, data]);

  const load = useCallback(async (signal) => {
    if (!/^\d{4}-\d{2}$/.test(data || "")) return;
    setLoading(true);
    setCellDrafts({});
    try {
      const [anno, mese] = data.split("-");
      const response = await api.get("/billing/import-files", { params: { anno, mese, _: Date.now() }, signal });
      const loadedFiles = response.data || [];
      setFiles(loadedFiles);
      if (loadedFiles.length) {
        const lines = await api.get(`/billing/import-files/${loadedFiles[0].id}/lines`, { signal });
        setDetail({ file: loadedFiles[0], lines: lines.data || [] });
      } else setDetail(null);
    } catch (error) {
      if (signal?.aborted || error?.code === "ERR_CANCELED") return;
      toast.error(error.response?.data?.message || "Errore caricamento import sezione 6");
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [data]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  useEffect(() => {
    if (!manualOpen || !manual.codice_univoco_azienda || !manual.prestazione) { setManualPriceLoading(false); setManualPriceMessage(""); return; }
    const controller = new AbortController();
    setManualPriceLoading(true);
    setManualPriceMessage("");
    const [anno, mese] = data.split("-");
    api.get("/billing/import-files/manual-price", { params: { anno, mese, codice_univoco_azienda: manual.codice_univoco_azienda, prestazione: manual.prestazione }, signal: controller.signal })
      .then(({ data: price }) => {
        if (controller.signal.aborted) return;
        setManual((old) => ({ ...old, importo_unitario: price.importo == null ? "" : String(price.importo) }));
        setManualPriceMessage(price.importo == null ? "Prezzo di listino non disponibile: inserisci il valore unitario." : "Importo di listino compilato. Puoi modificarlo.");
      }).catch((error) => {
        if (!controller.signal.aborted) setManualPriceMessage(error.response?.data?.message || "Impossibile caricare il listino: inserisci il valore unitario.");
      }).finally(() => { if (!controller.signal.aborted) setManualPriceLoading(false); });
    return () => controller.abort();
  }, [manualOpen, manual.codice_univoco_azienda, manual.prestazione, data]);

  const openManual = async () => {
    setManualIncludeCessati(false);
    setManual({ codice_univoco_azienda: "", prestazione: "", quantita: "1", importo_unitario: "", descrizione_extra_fattura: "" });
    setManualPriceLoading(false); setManualPriceMessage("");
    setManualBusy(true);
    try {
      const response = await api.get("/billing/import-files/manual-options");
      setManualOptions(response.data);
      setManualOpen(true);
    } catch (error) { toast.error(error.response?.data?.message || "Errore caricamento prestazioni sezione 6"); }
    finally { setManualBusy(false); }
  };
  const addManual = async () => {
    if (!manualValid || manualClosed || roleReadOnly) return;
    setManualBusy(true);
    try {
      const [anno, mese] = data.split("-");
      const response = await api.post("/billing/import-files/manual-lines", { ...manual, anno, mese });
      await load();
      const fileList = await api.get("/billing/import-files", { params: { anno, mese } });
      const file = fileList.data.find((item) => item.id === response.data.import_file_id);
      if (file) await openDetail(file);
      setDetailTipo(new Set()); setDetailSearch(""); setDetailOnlyCessati(false); setDetailOnlyPending(false);
      if (Number(manual.quantita) * Number(manual.importo_unitario) === 0) setDetailShowZero(true);
      setManualOpen(false);
      toast.success("Riga manuale aggiunta");
      onImported?.(data);
    } catch (error) { toast.error(error.response?.data?.message || "Errore inserimento riga manuale"); }
    finally { setManualBusy(false); }
  };

  const chooseFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setPending(file);
    setDestinazione(data);
    setPreview(null);
    setPreviewTipo(new Set());
    setPreviewSearch("");
    setPreviewSort({ field: "cliente", direction: "asc" });
    setPrezzi({});
  };

  const makeBody = () => {
    const [anno, mese] = destinazione.split("-");
    const body = new FormData();
    body.append("file", pending);
    body.append("anno", anno);
    body.append("mese", mese);
    return body;
  };

  const analyze = async () => {
    if (!pending || !/^\d{4}-(0[1-9]|1[0-2])$/.test(destinazione || "")) return;
    setUploading(true);
    try {
      const response = await api.post("/billing/import-files/preview", makeBody(), {
        headers: { "Content-Type": undefined },
      });
      setPreview(response.data);
      setPrezzi(Object.fromEntries((response.data.prezzi_mancanti || []).map((item) => [item.key, ""])));
    } catch (error) {
      toast.error(error.response?.data?.message || "Errore analisi file");
    } finally { setUploading(false); }
  };

  const upload = async () => {
    setUploading(true);
    try {
      const [anno, mese] = destinazione.split("-");
      const body = makeBody();
      body.append("prezzi", JSON.stringify((preview?.prezzi_mancanti || []).map((item) => ({ key: item.key, importo: prezzi[item.key] }))));
      await api.post("/billing/import-files", body, {
        headers: { "Content-Type": undefined },
      });
      toast.success(`File destinato a ${mesi[Number(mese) - 1]} ${anno}`);
      setPending(null);
      setPreview(null);
      if (destinazione === data) await load();
      onImported?.(destinazione);
    } catch (error) {
      toast.error(error.response?.data?.message || "Errore importazione file");
    } finally {
      setUploading(false);
    }
  };

  const download = async (item) => {
    try {
      const response = await api.get(`/billing/import-files/${item.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = item.nome_file;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.message || "Errore download file");
    }
  };

  const openDetail = async (item) => {
    if (detail?.file.id === item.id) { setDetail(null); return; }
    try {
      const response = await api.get(`/billing/import-files/${item.id}/lines`);
      setDetail({ file: item, lines: response.data || [] });
      setDetailTipo(new Set());
      setDetailSearch("");
      setCellDrafts({});
      setDetailShowZero(false);
      setDetailOnlyCessati(false);
      setDetailOnlyPending(false);
    } catch (error) { toast.error(error.response?.data?.message || "Errore caricamento dettaglio importazione"); }
  };

  const deleteImport = async (item) => {
    if (!window.confirm(`Eliminare l'importazione ${item.nome_file} e tutte le ${item.righe_importate || 0} voci collegate?`)) return;
    setDeleting(true);
    try {
      await api.delete(`/billing/import-files/${item.id}`);
      if (detail?.file.id === item.id) setDetail(null);
      // Rimuoviamo subito il blocco dalla vista: l'endpoint conserva lo storico
      // ma lo marca eliminato, quindi non deve riapparire neppure con una
      // risposta HTTP precedente rimasta nella cache del browser.
      setFiles((old) => old.filter((file) => file.id !== item.id));
      toast.success("Importazione eliminata");
    } catch (error) { toast.error(error.response?.data?.message || "Errore eliminazione importazione"); }
    finally { setDeleting(false); }
  };

  const deleteLine = async (line) => {
    if (!window.confirm(`Eliminare la voce ${line.prestazione} per ${line.ragione_sociale}?`)) return;
    setDeleting(true);
    try {
      await api.delete(`/billing/import-files/${detail.file.id}/lines/${line.id}`);
      setDetail((old) => ({ ...old, lines: old.lines.map((item) => item.id === line.id ? { ...item, stato: "esclusa" } : item),
        file: { ...old.file, righe_importate: Math.max(0, Number(old.file.righe_importate || 0) - 1) } }));
      setFiles((old) => old.map((item) => item.id === detail.file.id
        ? { ...item, righe_importate: Math.max(0, Number(item.righe_importate || 0) - 1) } : item));
      toast.success("Voce eliminata");
    } catch (error) { toast.error(error.response?.data?.message || "Errore eliminazione voce"); }
    finally { setDeleting(false); }
  };

  const discardCell = (key) => setCellDrafts((current) => {
    const next = { ...current };
    delete next[key];
    return next;
  });

  const saveCell = async (line, field, restoredValue) => {
    if (isLineClosed(line) || roleReadOnly || deleting || isDeleted(line)) return;
    const key = `${line.id}-${field}`;
    const draft = restoredValue ?? cellDrafts[key];
    const value = Number(draft);
    if (draft == null || String(draft).trim() === "" || !Number.isFinite(value) || value < 0) {
      toast.error("Inserisci un numero maggiore o uguale a zero"); return;
    }
    setDeleting(true);
    try {
      const response = await api.put(`/billing/import-files/${detail.file.id}/lines/${line.id}`, {
        quantita: Number(line.quantita), importo_unitario: Number(line.importo_unitario), importo_fisso: Number(line.importo_fisso || 0), campo_modificato: field, [field]: value,
      });
      setDetail((old) => ({ ...old, lines: old.lines.map((item) => item.id === line.id ? { ...item, ...response.data, [`${field}_precedente`]: item[`${field}_precedente`] ?? item[field] } : item) }));
      discardCell(key);
      toast.success(response.data.stato === "validata" ? "Voce aggiornata e validata" : "Voce aggiornata con importo forzato");
    } catch (error) { toast.error(error.response?.data?.message || "Errore modifica voce"); }
    finally { setDeleting(false); }
  };

  const restoreCell = (line, field) => {
    const original = line[`${field}_precedente`] ?? line[field];
    if (Number(original) === Number(line[field])) {
      discardCell(`${line.id}-${field}`);
      return;
    }
    return saveCell(line, field, original);
  };

  const editableNumberColumn = (field, headerName, width) => ({
    field, headerName, type: "number", width, align: "center", headerAlign: "center",
    valueGetter: ({ row }) => Number(row[field]),
    renderCell: ({ row }) => {
      if (field === "importo_fisso" && (row.tipologia_fatturazione === "Z" || row.importo_fisso == null)) return <span>—</span>;
      const key = `${row.id}-${field}`;
      const draft = cellDrafts[key];
      const changed = draft !== undefined && (draft === "" || Number(draft) !== Number(row[field]));
      const disabled = field !== "quantita" || isLineClosed(row) || roleReadOnly || deleting || isDeleted(row);
      const previous = row[`${field}_precedente`] ?? (changed ? row[field] : null);
      const differsFromOriginal = previous != null && Number(row[field]) !== Number(previous);
      const forced = changed || (row.stato === "forzata"
        && (differsFromOriginal || (previous == null && field === "importo_unitario")));
      const showBadge = changed || differsFromOriginal || forced;
      const status = forced ? "FORZATO" : row.stato === "validata" ? "OK" : String(row.stato || "").toUpperCase();
      return <div className="flex h-full w-full items-center">
        <div className="relative h-full min-w-0 flex-1">
        <span className="absolute right-[calc(50%+38px)] top-1/2 -translate-y-1/2 whitespace-nowrap">{showBadge && <span
          title={changed ? "Modifica da confermare" : previous != null ? "Valore originale prima di qualsiasi modifica" : status}
          className={`inline-block rounded px-1 py-0.5 text-[10px] font-bold ${forced ? "bg-amber-100 text-amber-800" : "bg-green-100 text-green-700"}`}>
          {status}{previous != null && ` · ${Number(previous).toLocaleString("it-IT", { maximumFractionDigits: 2 })}`}
        </span>}</span>
        <input type="number" min="0" step="0.01" value={draft ?? Number(row[field] || 0)}
          disabled={disabled} aria-label={`${headerName} ${row.ragione_sociale}`}
          onFocus={(event) => { if (Number(event.target.value) === 0) event.target.select(); }}
          onChange={(event) => setCellDrafts((current) => ({ ...current, [key]: event.target.value }))}
          onKeyDown={(event) => {
            event.stopPropagation();
            if (event.key === "Enter") { event.preventDefault(); event.currentTarget.blur(); }
            if (event.key === "Escape") discardCell(key);
          }}
          className={`absolute left-1/2 top-1/2 w-16 -translate-x-1/2 -translate-y-1/2 rounded border px-1.5 py-1 text-right ${showBadge ? "border-amber-400 bg-amber-50 font-semibold text-amber-800" : "border-gray-200 bg-white"} disabled:bg-gray-100 disabled:text-gray-500`} />
        <div className="absolute left-[calc(50%+38px)] top-1/2 flex -translate-y-1/2 items-center gap-1">{changed && <>
          <button type="button" title="OK · Approva modifica" aria-label={`Approva ${headerName} ${row.ragione_sociale}`}
            disabled={disabled} onClick={() => saveCell(row, field)}
            className="flex h-6 w-6 items-center justify-center rounded-full border border-green-300 bg-green-50 text-green-700 shadow-sm hover:bg-green-100 disabled:opacity-40"><MdCheck size={16} /></button>
          <button type="button" title="Ripristina il valore originale" aria-label={`Rifiuta ${headerName} ${row.ragione_sociale}`}
            disabled={disabled} onClick={() => restoreCell(row, field)}
            className="flex h-6 w-6 items-center justify-center rounded-full border border-red-300 bg-red-50 text-red-600 shadow-sm hover:bg-red-100 disabled:opacity-40"><MdClose size={16} /></button>
        </>}</div>
        </div>
      </div>;
    },
  });

  const detailColumns = [
    { field: "tipologia_fatturazione", headerName: "Tipo", width: 65,
      renderCell: ({ value }) => <span className={`rounded border px-1.5 py-0.5 text-xs font-bold ${tipoClass(value)}`}>{value || "—"}</span> },
    { field: "ragione_sociale", headerName: "Cliente", flex: 1, minWidth: 250,
      renderCell: ({ row, value }) => <div className="flex items-center gap-1.5 min-w-0">
        <span className="truncate">{value}</span>
        {row.data_fine_elaborazioni && <span className="shrink-0 px-1.5 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-700">cessato</span>}
      </div> },
    { field: "referente_payroll1", headerName: "Referente payroll 1", width: 175 },
    { field: "referente_assunzioni", headerName: "Referente assunzioni", width: 185 },
    { field: "codice_ago", headerName: "Codice AGO", width: 160,
      valueGetter: ({ row }) => String(row.codice_ago || row.prestazione || "").replace(/^0+(?=\d)/, ""),
      sortComparator: (a, b) => String(a).localeCompare(String(b), "it", { numeric: true }) },
    { field: "descrizione_prestazione", headerName: "Descrizione (CRM)", flex: 1.2, minWidth: 250 },
    { field: "descrizione_extra_fattura", headerName: "Ulteriore descrizione in fattura", minWidth: 270, flex: 1,
      renderCell: ({ value }) => <span className="truncate" title={value || ""}>{value || "—"}</span> },
    { field: "fascia_iipay", headerName: "Fascia iiPay", width: 300, align: "center", headerAlign: "center", sortable: false, filterable: false,
      renderCell: ({ row }) => <IipayFasciaDetails row={row} /> },
    editableNumberColumn("quantita", "Quantità", 225),
    editableNumberColumn("importo_unitario", "Valore unitario", 225),
    editableNumberColumn("importo_fisso", "Importo fisso", 190),
    {field:'bpoint_1415_province',headerName:'Province 1415',width:120,valueGetter:({row})=>Number(row.codice_ago)===1415?row.bpoint_1415_province:'—'},
    { field: "supplementi1422", headerName: "Supplementi 1422", width: 210,
      valueGetter: ({ row }) => Number(row.codice_ago) !== 1422 ? "—" : [
        Number(row.bpoint_1422_rateazione) === 1 && "Rateazione",
        Number(row.bpoint_1422_presunto) === 1 && "Riduzione presunto",
      ].filter(Boolean).join(" · ") || "Nessuno" },
    { field: "totale", headerName: "Totale", type: "number", width: 130, align: "center", headerAlign: "center",
      valueGetter: ({ row }) => Number(row.totale),
      renderCell: ({ value }) => <strong>{Number(value).toLocaleString("it-IT", { style: "currency", currency: "EUR" })}</strong> },
    { field: "azioni", headerName: "", width: 55, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: ({ row }) => !roleReadOnly && !isDeleted(row) && <button type="button" disabled={isLineClosed(row) || deleting}
        onClick={() => deleteLine(row)} title="Elimina voce" aria-label={`Elimina voce ${row.ragione_sociale}`}
        className="rounded p-2 text-red-700 hover:bg-red-50 disabled:opacity-40"><MdDelete size={18} /></button> },
  ];

  return <div className="el-container p-5">
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <h2 className="text-xl font-semibold text-gray-800">Sezione 6 · Import file e righe manuali</h2>
        <p className="mt-1 text-sm text-gray-500">Prima dell'importazione vengono controllati clienti, prestazioni, quantità, duplicati e prezzi di listino.</p>
      </div>
      <input ref={inputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={chooseFile} />
      <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
      <div className="flex flex-wrap items-center justify-end gap-2"><BillingClosureBadges state={closure} /></div>
      <button type="button" disabled={roleReadOnly || manualBusy} onClick={openManual}
        className="inline-flex items-center gap-2 rounded-md border border-blue-600 px-4 py-2 font-semibold text-blue-600 hover:bg-blue-50 disabled:opacity-50">
        <MdAdd size={20} /> Aggiungi riga
      </button>
      <button type="button" disabled={roleReadOnly} onClick={() => inputRef.current?.click()}
        className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">
        <MdCloudUpload size={20} /> Importa Excel
      </button>
      </div>
    </div>
    <div className="mt-5 overflow-x-auto rounded-md border border-gray-200">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-left text-gray-600"><tr><th className="px-4 py-3">File</th><th className="px-4 py-3">Caricato da</th><th className="px-4 py-3">Data</th><th className="px-4 py-3">Stato</th><th className="px-4 py-3 text-right">Azioni</th></tr></thead>
        <tbody className="divide-y divide-gray-100">
          {!loading && files.length === 0 && <tr><td colSpan="5" className="px-4 py-8 text-center text-gray-500">Nessun file importato per questo mese.</td></tr>}
          {loading && <tr><td colSpan="5" className="px-4 py-8 text-center text-gray-500">Caricamento...</td></tr>}
          {files.map((item) => <tr key={item.id}>
            <td className="px-4 py-3 font-medium text-gray-800">{item.nome_file}<div className="text-xs font-normal text-gray-400">{item.mime_type === "application/x-crm-manual" ? "Inserimenti manuali" : `${Math.ceil(Number(item.dimensione) / 1024)} KB`}</div></td>
            <td className="px-4 py-3">{item.uploaded_by || "—"}</td><td className="px-4 py-3">{new Date(item.created_at).toLocaleString("it-IT")}</td>
            <td className="px-4 py-3"><span className={`rounded px-2 py-1 text-xs font-semibold ${item.stato === "elaborato" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{item.stato === "elaborato" ? `${item.righe_importate} righe${item.mime_type === "application/x-crm-manual" ? " manuali" : " importate"}` : "In attesa"}</span></td>
            <td className="px-4 py-3 text-right whitespace-nowrap">
              <button type="button" onClick={() => openDetail(item)} title="Vedi singole voci" className="rounded p-2 text-slate-700 hover:bg-slate-100"><MdVisibility size={20} /></button>
              {item.mime_type !== "application/x-crm-manual" && <button type="button" onClick={() => download(item)} title="Scarica originale" className="rounded p-2 text-blue-700 hover:bg-blue-50"><MdDownload size={20} /></button>}
              {!roleReadOnly && <button type="button" disabled={deleting} onClick={() => deleteImport(item)} title="Elimina tutta l'importazione" className="rounded p-2 text-red-700 hover:bg-red-50 disabled:opacity-40"><MdDelete size={20} /></button>}
            </td>
          </tr>)}
        </tbody>
      </table>
    </div>
    <Modal isOpen={manualOpen} closeButton={() => !manualBusy && setManualOpen(false)} size="compact" headerText="Aggiungi riga · Sezione 6"
      rightButtons={<button type="button" disabled={manualBusy} onClick={() => setManualOpen(false)} className="rounded border px-4 py-2">Annulla</button>}
      leftButtons={<button type="button" disabled={manualBusy || !manualValid || manualClosed || roleReadOnly} onClick={addManual} className="rounded bg-blue-600 px-4 py-2 font-semibold text-white disabled:opacity-50">Conferma inserimento</button>}>
      <div className="space-y-4 p-3">
        <p className="text-sm text-gray-500">Mese di destinazione: {data}. Seleziona una prestazione della sezione 6.</p>
        <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-[220px] flex-1">
        <Autocomplete label="Cliente" placeholder="Cerca cliente..." value={manual.codice_univoco_azienda || ""} disabled={manualBusy}
          options={manualOptions.clienti.filter((item) => manualIncludeCessati || !item.data_fine_elaborazioni).map((item) => ({ id: item.codice_univoco, name: `${item.ragione_sociale}${item.data_fine_elaborazioni ? " (cessato)" : ""}`, badge: item.tipologia_fatturazione }))}
          onChange={(event) => {
            const cliente = manualOptions.clienti.find((item) => item.codice_univoco === event.target.value);
            const voce = manualOptions.prestazioni.find((item) => String(item.codice) === String(manual.prestazione));
            const compatibile = cliente && voce && (!voce.tipologia_fatturazione || voce.tipologia_fatturazione === cliente.tipologia_fatturazione || (cliente.tipologia_fatturazione === "B" && voce.tipologia_fatturazione === "Z"));
            setManual((old) => ({ ...old, codice_univoco_azienda: event.target.value, prestazione: compatibile ? old.prestazione : "", importo_unitario: "" }));
          }} />
        </div>
        <label className="inline-flex items-center gap-2 whitespace-nowrap text-sm text-gray-600">
          <input type="checkbox" checked={manualIncludeCessati} disabled={manualBusy}
            onChange={(event) => {
              setManualIncludeCessati(event.target.checked);
              if (!event.target.checked && manualClient?.data_fine_elaborazioni) {
                setManual((old) => ({ ...old, codice_univoco_azienda: "", prestazione: "", importo_unitario: "" }));
              }
            }} />
          Includi cessati
        </label>
        </div>
        <Autocomplete label="Codice prestazione · Sezione 6" placeholder="Cerca codice o descrizione..." value={manual.prestazione || ""} disabled={manualBusy || !manualClient}
          options={manualCodes.map((item) => ({ id: item.codice, name: `${item.codice} · ${item.descrizione_voce_crm || ""}` }))}
          onChange={(event) => setManual((old) => ({ ...old, prestazione: event.target.value, importo_unitario: "" }))} />
        <label className="block text-sm text-gray-600">Ulteriore descrizione in fattura
          <textarea rows={3} maxLength={500} disabled={manualBusy} value={manual.descrizione_extra_fattura || ""}
            onChange={(event) => setManual((old) => ({ ...old, descrizione_extra_fattura: event.target.value }))}
            placeholder="Descrizione aggiuntiva da riportare in fattura (facoltativa)" className="mt-1 block w-full rounded border p-2" />
        </label>
        {(manualPriceLoading || manualPriceMessage) && <p className="text-sm text-gray-500">{manualPriceLoading ? "Caricamento importo di listino..." : manualPriceMessage}</p>}
        <div className="grid grid-cols-2 gap-3">
          {[["quantita", "Quantità"], ["importo_unitario", "Valore unitario"]].map(([field, label]) => <label key={field} className="text-sm text-gray-600">{label}
            <input type="number" min="0" step="0.01" disabled={manualBusy || (field === "importo_unitario" && manualPriceLoading)} value={manual[field] ?? ""}
              onChange={(event) => setManual((old) => ({ ...old, [field]: event.target.value }))} className="mt-1 block w-full rounded border p-2 text-right" />
          </label>)}
          <div className="text-sm text-gray-600">Totale<div className="mt-1 p-2 text-right font-semibold">{((Number(manual.quantita) || 0) * (Number(manual.importo_unitario) || 0)).toLocaleString("it-IT", { style: "currency", currency: "EUR" })}</div></div>
        </div>
      </div>
    </Modal>
    <Modal isOpen={Boolean(pending)} closeButton={() => !uploading && setPending(null)} size="compact" headerText={preview ? "Anteprima e anomalie" : "Destinazione dell'importazione"}
      rightButtons={<button type="button" onClick={() => setPending(null)} disabled={uploading} className="rounded border border-gray-300 px-4 py-2">Annulla</button>}
      leftButtons={<button type="button" onClick={preview ? upload : analyze}
        disabled={uploading || !destinazione || (preview && (preview.riepilogo?.anomalie_bloccanti > 0 || (preview.prezzi_mancanti || []).some((p) => prezzi[p.key] === "" || Number(prezzi[p.key]) < 0)))}
        className="rounded bg-blue-600 px-4 py-2 font-semibold text-white disabled:opacity-50">{uploading ? "Elaborazione..." : preview ? "Conferma importazione" : "Analizza file"}</button>}>
      <div className="space-y-4 p-3">
        <p className="text-sm text-gray-600">File: <strong>{pending?.name}</strong></p>
        {!preview && <label className="block text-sm font-semibold text-gray-700">Mese e anno di destinazione<input type="month" value={destinazione || ""} onChange={(e) => setDestinazione(e.target.value)} className="mt-2 block w-full rounded border border-gray-300 p-2 font-normal" /></label>}
        {preview && <>
          <div className="grid grid-cols-3 gap-3 text-center text-sm"><div className="rounded bg-green-50 p-3"><strong className="block text-lg text-green-700">{preview.riepilogo?.righe_valide}</strong>Righe valide</div><div className="rounded bg-yellow-50 p-3"><strong className="block text-lg text-yellow-700">{preview.riepilogo?.duplicati_esclusi}</strong>Duplicati esclusi</div><div className="rounded bg-red-50 p-3"><strong className="block text-lg text-red-700">{preview.riepilogo?.anomalie_bloccanti}</strong>Anomalie bloccanti</div></div>
          <div className="flex flex-wrap items-center gap-2"><span className="text-xs font-semibold uppercase text-gray-500">Separa per gestionale</span>
            {TIPI.map(([id, label, colors]) => <button key={id} type="button" title={`${label}: ${(preview.righe || []).filter((r) => !r.esclusa && r.tipologia_fatturazione === id).length} voci`} onClick={() => setPreviewTipo((current) => { const next = new Set(current); next.has(id) ? next.delete(id) : next.add(id); return next; })} className={`px-2.5 py-0.5 rounded text-xs font-semibold border transition-colors ${previewTipo.has(id) ? `${colors} ring-1 ring-current` : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"}`}>{id}</button>)}
            <input type="search" value={previewSearch} onChange={(event) => setPreviewSearch(event.target.value)} placeholder="Cerca cliente o prestazione..." className="ml-auto min-w-[240px] rounded border border-gray-300 px-3 py-1.5 text-sm font-normal text-gray-700" />
          </div>
          <div><h4 className="mb-2 font-semibold">Voci pronte per l'importazione</h4><div className="max-h-64 overflow-auto rounded border"><table className="w-full text-xs"><thead className="sticky top-0 bg-gray-50"><tr>{[["tipo", "Tipo", "text-left"], ["cliente", "Cliente", "text-left"], ["prestazione", "Prestazione", "text-left"], ["quantita", "Quantità", "text-right"], ["prezzo", "Prezzo", "text-right"], ["totale", "Totale", "text-right"], ["origine", "Origine", "text-left"]].map(([field, label, align]) => <th key={field} className={`p-2 ${align}`}><button type="button" onClick={() => sortPreview(field)} className="whitespace-nowrap font-semibold hover:text-blue-700">{sortLabel(field, label)}</button></th>)}</tr></thead><tbody>{previewRows.map((r, index) => <tr key={`${r.riga_excel}-${r.coppia}-${index}`} className="border-t"><td className="p-2"><span className={`rounded border px-1.5 py-0.5 font-bold ${tipoClass(r.tipologia_fatturazione)}`}>{r.tipologia_fatturazione || "—"}</span></td><td className="p-2">{r.codice}<div className="text-[11px] text-gray-500">{r.ragione_sociale}</div></td><td className="p-2">{r.prestazione || r.voce} · {r.descrizione}</td><td className="p-2 text-right">{r.quantita}{(r.calcolo_bpoint_1420 || r.calcolo_bpoint_1415) && <div className="text-[10px] text-blue-700">solo selezione fascia</div>}{r.calcolo_bpoint_1421 && Number(r.calcolo_bpoint_1421.quantita_fatturata) !== Number(r.quantita) && <div className="text-[10px] text-blue-700">fatturate: {r.calcolo_bpoint_1421.quantita_fatturata}</div>}</td><td className="p-2 text-right font-semibold">{r.prezzo == null ? "da inserire" : `${Number(r.prezzo).toFixed(2)} €`}</td><td className="p-2 text-right font-semibold">{r.prezzo == null ? "—" : `${Number(r.totale ?? (r.quantita * r.prezzo)).toFixed(2)} €`}</td><td className="p-2 text-gray-600">{r.origine_prezzo || "prezzo richiesto"}{r.calcolo_bpoint_1422 && <div className="text-[10px]">Rateazione: {r.calcolo_bpoint_1422.rateazione ? "sì" : "no"} · Riduzione presunto: {r.calcolo_bpoint_1422.riduzione_presunto ? "sì" : "no"} · supplementi {r.calcolo_bpoint_1422.importo_fisso.toFixed(2)} €</div>}<IipayFasciaBadge row={r} /></td></tr>)}{previewRows.length === 0 && <tr><td colSpan="7" className="p-6 text-center text-gray-500">Nessuna voce trovata.</td></tr>}</tbody></table></div></div>
          {(preview.anomalie || []).length > 0 && <div><h4 className="mb-2 font-semibold">Anomalie rilevate</h4><div className="max-h-40 overflow-auto rounded border"><table className="w-full text-xs"><thead className="bg-gray-50"><tr><th className="p-2 text-left">Riga</th><th className="p-2 text-left">Cliente</th><th className="p-2 text-left">Voce</th><th className="p-2 text-left">Anomalia</th></tr></thead><tbody>{preview.anomalie.map((a, i) => <tr key={`${a.riga_excel}-${i}`} className="border-t"><td className="p-2">{a.riga_excel}</td><td className="p-2">{a.codice} · {a.ragione_sociale}</td><td className="p-2">{a.voce}</td><td className={`p-2 font-semibold ${a.bloccante ? "text-red-700" : "text-yellow-700"}`}>{a.tipo.replaceAll("_", " ")}</td></tr>)}</tbody></table></div></div>}
          {(preview.prezzi_mancanti || []).some((p) => previewTipo.size === 0 || previewTipo.has(p.tipologia_fatturazione)) && <div><h4 className="mb-1 font-semibold">Prezzi mancanti nel listino cliente</h4><p className="mb-2 text-xs text-gray-500">I valori inseriti saranno memorizzati nel listino e riutilizzati nei prossimi import.</p><div className="max-h-64 overflow-auto rounded border"><table className="w-full text-sm"><thead className="bg-gray-50"><tr><th className="p-2 text-left">Cliente</th><th className="p-2 text-left">Prestazione</th><th className="p-2 text-right">Prezzo unitario</th></tr></thead><tbody>{preview.prezzi_mancanti.filter((p) => previewTipo.size === 0 || previewTipo.has(p.tipologia_fatturazione)).map((p) => <tr key={p.key} className="border-t"><td className="p-2">{p.codice}<div className="text-xs text-gray-500">{p.ragione_sociale}</div></td><td className="p-2">{p.prestazione} · {p.descrizione}</td><td className="p-2 text-right"><input type="number" min="0" step="0.01" value={prezzi[p.key] ?? ""} onChange={(e) => setPrezzi((old) => ({ ...old, [p.key]: e.target.value }))} className="w-28 rounded border p-2 text-right" /> €</td></tr>)}</tbody></table></div></div>}
        </>}
      </div>
    </Modal>
    {!detail && !loading && files.length > 0 && <div className="mt-5">
      <h3 className="mb-3 text-lg font-semibold text-gray-800">Totale sezione 6</h3>
      {sectionLines === null ? <p className="text-sm text-gray-500">Caricamento totali...</p>
        : <TotalSectionCard total={sectionTotals.total} totalsByType={sectionTotals.byType} onClick={() => setSectionTotalsOpen(true)} />}
    </div>}
    {!detail && sectionTotalsOpen && sectionLines && <ImportFileTotals rows={sectionLines} fileName="Tutta la sezione 6" onClose={() => setSectionTotalsOpen(false)} />}
    {detail && totalsOpen && <ImportFileTotals rows={detail.lines || []} fileName={detail.file.nome_file} onClose={() => setTotalsOpen(false)} />}
    {detail && <div className="mt-5 rounded-md border border-gray-200 bg-white p-4">
      <div className="mb-3 flex flex-wrap items-center gap-3"><h3 className="text-lg font-semibold text-gray-800">Voci · {detail.file.nome_file}</h3><button type="button" onClick={() => setDetail(null)} className="ml-auto rounded border border-gray-300 px-3 py-1.5 text-sm">Nascondi voci</button></div>
      <TotalSectionCard total={detailTotals.total} totalsByType={detailTotals.byType} onClick={() => setTotalsOpen(true)} className="mb-3" />
      <div className="mb-3 flex flex-wrap items-center gap-2">{TIPI.map(([id, label, colors]) => <button key={id} type="button" title={label} onClick={() => setDetailTipo((current) => { const next = new Set(current); next.has(id) ? next.delete(id) : next.add(id); return next; })} className={`px-2.5 py-0.5 rounded text-xs font-semibold border transition-colors ${detailTipo.has(id) ? `${colors} ring-1 ring-current` : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"}`}>{id}</button>)}<button type="button" aria-pressed={detailOnlyCessati} onClick={() => setDetailOnlyCessati((value) => !value)}
        className={`text-xs rounded px-2 py-1 font-medium border transition-colors ${detailOnlyCessati ? "bg-slate-200 text-slate-700 border-slate-400" : "bg-gray-100 text-gray-500 border-gray-300"}`}>
        {detailOnlyCessati ? "Tutti" : "Solo cessati"}
      </button><button type="button" aria-pressed={detailShowDeleted} onClick={() => setDetailShowDeleted((value) => !value)}
        className={`text-xs rounded px-2 py-1 font-medium border transition-colors ${detailShowDeleted ? "bg-red-100 text-red-700 border-red-300" : "bg-gray-100 text-gray-500 border-gray-300"}`}>
        {detailShowDeleted ? "Nascondi eliminati" : "Mostra eliminati"}
      </button><button type="button" aria-pressed={detailShowZero} onClick={() => setDetailShowZero((value) => !value)}
        className={`rounded border px-2 py-1 text-xs font-semibold ${detailShowZero ? "bg-slate-200 text-slate-700 border-slate-400" : "bg-gray-100 text-gray-500 border-gray-300"}`}>
        {detailShowZero ? "Nascondi zero" : "Mostra zero"}
      </button><button type="button" aria-pressed={detailOnlyPending} onClick={() => setDetailOnlyPending((value) => !value)}
        className={`rounded border px-2 py-1 text-xs font-semibold ${detailOnlyPending ? "bg-amber-100 text-amber-800 border-amber-400" : "bg-white text-slate-600 border-gray-300"}`}>
        {detailOnlyPending ? "Mostra tutti gli stati" : "Mostra forzati e bozze"}
      </button><input type="search" value={detailSearch} onChange={(event) => setDetailSearch(event.target.value)} placeholder="Cerca cliente o voce..." className="ml-auto min-w-[240px] rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700" /></div>
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white" style={{ height: "60vh", minHeight: 400 }}>
        <DataGridPro rows={detailRows} columns={detailColumns} density="compact" disableRowSelectionOnClick
          localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
          initialState={{ sorting: { sortModel: [{ field: "ragione_sociale", sort: "asc" }] } }}
          getRowClassName={({ row }) => isDeleted(row) ? "opacity-30 line-through" : Number(row.totale) === 0 ? "import-totale-zero" : ""}
          sx={{ "& .import-totale-zero": { backgroundColor: "#fffbeb", color: "#92400e" } }} />
      </div>
      {!roleReadOnly && <div className="mt-3 text-right"><button type="button" disabled={deleting} onClick={() => deleteImport(detail.file)} className="rounded border border-red-300 px-4 py-2 font-semibold text-red-700 disabled:opacity-40">Elimina tutto il blocco</button></div>}
    </div>}
  </div>;
};
